(() => {
  const SHORT_ALIASES = new Set([
    "sls",
    "sds",
    "sles",
    "slsa",
    "hap",
    "nhap",
    "n hap",
    "mfp",
    "cpc",
    "acp",
    "tio2",
    "peg",
    "pvp",
    "cmc",
  ]);

  const CATEGORY_ORDER = [
    "remineralizing",
    "fluoride",
    "sensitivity",
    "whitening",
    "antimicrobial",
    "surfactant",
    "abrasive",
    "tartar",
    "humectant",
    "sweetener",
    "flavor",
    "color",
    "preservative",
    "thickener",
    "solvent",
    "pH",
    "other",
  ];

  const CATEGORY_LABELS = {
    remineralizing: "Remineralizing",
    fluoride: "Fluoride",
    sensitivity: "Sensitivity",
    whitening: "Whitening",
    antimicrobial: "Antimicrobial",
    surfactant: "Surfactant / foam",
    abrasive: "Abrasive",
    tartar: "Tartar control",
    humectant: "Humectant",
    sweetener: "Sweetener",
    flavor: "Flavor",
    color: "Color",
    preservative: "Preservative",
    thickener: "Thickener / binder",
    solvent: "Solvent / base",
    pH: "pH adjuster",
    other: "Other",
  };

  const SNAPSHOTS = [
    { id: "hap", label: "Hydroxyapatite", flag: "hap" },
    { id: "fluoride", label: "Fluoride", flag: "fluoride" },
    { id: "sls", label: "SLS", flag: "sls" },
    { id: "sles", label: "SLES", flag: "sles" },
    { id: "dye", label: "Artificial dyes", flag: "dye" },
    { id: "xylitol", label: "Xylitol", flag: "xylitol" },
    { id: "artificial_sweetener", label: "Artificial sweeteners", flag: "artificial_sweetener" },
    { id: "paraben", label: "Parabens", flag: "paraben" },
    { id: "peroxide", label: "Peroxide", flag: "peroxide" },
    { id: "triclosan", label: "Triclosan", flag: "triclosan" },
    { id: "charcoal", label: "Charcoal", flag: "charcoal" },
    { id: "titanium_dioxide", label: "Titanium dioxide", flag: "titanium_dioxide" },
  ];

  const SKIP_TOKENS = new Set([
    "active ingredient",
    "active ingredients",
    "inactive ingredient",
    "inactive ingredients",
    "other ingredients",
    "purpose",
    "anticavity",
    "antigingivitis",
    "toothpaste",
    "drug facts",
    "uses",
    "warnings",
    "directions",
    "questions",
    "ingredients",
  ]);

  /** @type {Array<{id: string, name: string, aliases: string[], category: string, flags: string[], summary: string, detail?: string}>} */
  const INGREDIENTS = [
    {
      id: "hydroxyapatite",
      name: "Hydroxyapatite",
      aliases: [
        "hydroxyapatite",
        "hydroxylapatite",
        "calcium hydroxyapatite",
        "nano hydroxyapatite",
        "nano-hydroxyapatite",
        "micro hydroxyapatite",
        "micro-hydroxyapatite",
        "medical hydroxyapatite",
        "synthetic hydroxyapatite",
        "hap",
        "n-hap",
        "nhap",
        "n hap",
        "nano hap",
        "ca10(po4)6(oh)2",
      ],
      category: "remineralizing",
      flags: ["hap"],
      summary:
        "A calcium phosphate mineral closely related to the mineral in enamel and dentin. Used in some fluoride-free toothpastes.",
      detail:
        "Nano and micro describe particle-size ranges of the same mineral. Published studies have examined hydroxyapatite toothpastes for remineralization, sensitivity, and caries endpoints; results depend on the formula and study design.",
    },
    {
      id: "sodium-fluoride",
      name: "Sodium fluoride",
      aliases: ["sodium fluoride", "naf", "na fluoride"],
      category: "fluoride",
      flags: ["fluoride"],
      summary:
        "A common fluoride salt used as an anticavity active in many toothpastes, often listed with a percentage or ppm fluoride ion.",
      detail:
        "Typical adult toothpastes in the US use about 1000–1450 ppm fluoride. Fluoride toothpaste remains the most studied cavity-prevention toothpaste. Spit after brushing; do not swallow toothpaste.",
    },
    {
      id: "stannous-fluoride",
      name: "Stannous fluoride",
      aliases: ["stannous fluoride", "tin fluoride", "snf2"],
      category: "fluoride",
      flags: ["fluoride"],
      summary:
        "A fluoride salt that also supplies tin. Used as an anticavity active; some formulas are also studied for gingivitis and sensitivity.",
      detail:
        "Stannous formulas can taste metallic or temporarily stain in some people. Stability and the rest of the formula affect how the tin and fluoride behave.",
    },
    {
      id: "sodium-monofluorophosphate",
      name: "Sodium monofluorophosphate",
      aliases: [
        "sodium monofluorophosphate",
        "monofluorophosphate",
        "mfp",
        "smfp",
      ],
      category: "fluoride",
      flags: ["fluoride"],
      summary:
        "Another fluoride source used as an anticavity active. Often abbreviated MFP on labels.",
    },
    {
      id: "olaflur",
      name: "Olaflur (amine fluoride)",
      aliases: ["olaflur", "amine fluoride"],
      category: "fluoride",
      flags: ["fluoride"],
      summary:
        "An organic amine fluoride used as an anticavity active, more common in some European formulas than in US pastes.",
    },
    {
      id: "potassium-nitrate",
      name: "Potassium nitrate",
      aliases: ["potassium nitrate"],
      category: "sensitivity",
      flags: ["sensitivity"],
      summary:
        "A common desensitizing ingredient. It is studied for reducing nerve response in exposed dentin, not for remineralizing enamel.",
    },
    {
      id: "potassium-citrate",
      name: "Potassium citrate",
      aliases: ["potassium citrate"],
      category: "sensitivity",
      flags: ["sensitivity"],
      summary:
        "A potassium salt used in some sensitivity formulas. Potassium salts are studied for calming nerve response in dentin.",
    },
    {
      id: "strontium-chloride",
      name: "Strontium chloride",
      aliases: ["strontium chloride"],
      category: "sensitivity",
      flags: ["sensitivity"],
      summary:
        "An older desensitizing salt. Less common in current US mass-market pastes than potassium nitrate.",
    },
    {
      id: "arginine",
      name: "Arginine",
      aliases: ["arginine", "l-arginine", "arginine bicarbonate"],
      category: "sensitivity",
      flags: ["sensitivity"],
      summary:
        "An amino acid used in some sensitivity toothpastes, often with calcium carbonate, to interact with exposed dentin.",
    },
    {
      id: "zinc-citrate",
      name: "Zinc citrate",
      aliases: ["zinc citrate"],
      category: "antimicrobial",
      flags: [],
      summary:
        "A zinc salt used to help control plaque bacteria and oral malodor. Also appears in some tartar-control formulas.",
    },
    {
      id: "zinc-oxide",
      name: "Zinc oxide",
      aliases: ["zinc oxide"],
      category: "antimicrobial",
      flags: [],
      summary:
        "A zinc compound used for plaque and odor control in some oral-care formulas.",
    },
    {
      id: "zinc-lactate",
      name: "Zinc lactate",
      aliases: ["zinc lactate"],
      category: "antimicrobial",
      flags: [],
      summary: "A zinc salt used similarly to zinc citrate for plaque and malodor control.",
    },
    {
      id: "zinc-chloride",
      name: "Zinc chloride",
      aliases: ["zinc chloride"],
      category: "antimicrobial",
      flags: [],
      summary: "A soluble zinc salt used in some antimicrobial or astringent oral-care formulas.",
    },
    {
      id: "stannous-chloride",
      name: "Stannous chloride",
      aliases: ["stannous chloride", "tin chloride"],
      category: "antimicrobial",
      flags: [],
      summary:
        "A tin salt used in some stannous systems. Tin compounds are studied for plaque and gingivitis; they can affect taste or staining.",
    },
    {
      id: "hydrogen-peroxide",
      name: "Hydrogen peroxide",
      aliases: ["hydrogen peroxide", "h2o2"],
      category: "whitening",
      flags: ["peroxide", "whitening"],
      summary:
        "A bleaching agent used in some whitening toothpastes and gels. Concentration in daily toothpaste is usually much lower than in in-office bleaching.",
    },
    {
      id: "carbamide-peroxide",
      name: "Carbamide peroxide",
      aliases: ["carbamide peroxide", "urea peroxide"],
      category: "whitening",
      flags: ["peroxide", "whitening"],
      summary:
        "A peroxide compound that breaks down into hydrogen peroxide and urea. Used in some whitening products.",
    },
    {
      id: "calcium-peroxide",
      name: "Calcium peroxide",
      aliases: ["calcium peroxide"],
      category: "whitening",
      flags: ["peroxide", "whitening"],
      summary: "A peroxide salt used in some whitening toothpastes as an oxygen-releasing agent.",
    },
    {
      id: "sodium-hexametaphosphate",
      name: "Sodium hexametaphosphate",
      aliases: ["sodium hexametaphosphate", "shmp"],
      category: "tartar",
      flags: ["whitening"],
      summary:
        "A polyphosphate used to help control tartar and surface stains. It does not bleach the underlying tooth color.",
    },
    {
      id: "tetrasodium-pyrophosphate",
      name: "Tetrasodium pyrophosphate",
      aliases: ["tetrasodium pyrophosphate", "tspp"],
      category: "tartar",
      flags: [],
      summary: "A pyrophosphate salt used in tartar-control toothpastes to help limit calculus buildup.",
    },
    {
      id: "tetrapotassium-pyrophosphate",
      name: "Tetrapotassium pyrophosphate",
      aliases: ["tetrapotassium pyrophosphate"],
      category: "tartar",
      flags: [],
      summary: "A potassium pyrophosphate used similarly to tetrasodium pyrophosphate for tartar control.",
    },
    {
      id: "disodium-pyrophosphate",
      name: "Disodium pyrophosphate",
      aliases: ["disodium pyrophosphate", "sodium acid pyrophosphate"],
      category: "tartar",
      flags: [],
      summary: "A pyrophosphate used in some tartar-control and stain-prevention formulas.",
    },
    {
      id: "pentasodium-triphosphate",
      name: "Pentasodium triphosphate",
      aliases: [
        "pentasodium triphosphate",
        "sodium tripolyphosphate",
        "sodium triphosphate",
      ],
      category: "tartar",
      flags: ["whitening"],
      summary:
        "A polyphosphate used for tartar control and to help loosen surface stains.",
    },
    {
      id: "pvm-ma",
      name: "PVM/MA copolymer",
      aliases: ["pvm/ma copolymer", "pvm ma copolymer", "gantrez"],
      category: "tartar",
      flags: [],
      summary:
        "A copolymer used in some tartar-control pastes to help keep polyphosphates on the tooth surface.",
    },
    {
      id: "sls",
      name: "Sodium lauryl sulfate (SLS)",
      aliases: [
        "sodium lauryl sulfate",
        "sodium dodecyl sulfate",
        "sls",
        "sds",
        "sodium lauryl sulphate",
      ],
      category: "surfactant",
      flags: ["sls"],
      summary:
        "A detergent that helps toothpaste spread and foam. Some people find it irritating, especially with recurrent canker sores.",
      detail:
        "SLS is very common in conventional toothpastes. It is not the same as sodium laureth sulfate (SLES) or sodium lauryl sulfoacetate (SLSa).",
    },
    {
      id: "sles",
      name: "Sodium laureth sulfate (SLES)",
      aliases: [
        "sodium laureth sulfate",
        "sodium lauryl ether sulfate",
        "sles",
        "sodium laureth sulphate",
      ],
      category: "surfactant",
      flags: ["sles"],
      summary:
        "An ethoxylated surfactant related to SLS. Used for foam and spread. It is a different INCI name from sodium lauryl sulfate.",
    },
    {
      id: "sodium-coco-sulfate",
      name: "Sodium coco-sulfate",
      aliases: ["sodium coco-sulfate", "sodium coco sulfate", "scs"],
      category: "surfactant",
      flags: [],
      summary:
        "A coconut-derived sulfate surfactant. Chemically related to SLS, but it is a different INCI name and is often a mixed alkyl sulfate.",
    },
    {
      id: "ammonium-lauryl-sulfate",
      name: "Ammonium lauryl sulfate",
      aliases: ["ammonium lauryl sulfate", "als"],
      category: "surfactant",
      flags: ["sls"],
      summary:
        "A lauryl sulfate surfactant similar in function to sodium lauryl sulfate.",
    },
    {
      id: "cocamidopropyl-betaine",
      name: "Cocamidopropyl betaine",
      aliases: ["cocamidopropyl betaine", "capb"],
      category: "surfactant",
      flags: [],
      summary:
        "A milder amphoteric surfactant often used with or instead of SLS. A known contact allergen for some people.",
    },
    {
      id: "sodium-cocoyl-glutamate",
      name: "Sodium cocoyl glutamate",
      aliases: ["sodium cocoyl glutamate"],
      category: "surfactant",
      flags: [],
      summary:
        "An amino-acid-based surfactant used as a gentler foaming agent. This is the surfactant in Klyyr One+.",
    },
    {
      id: "sodium-lauroyl-sarcosinate",
      name: "Sodium lauroyl sarcosinate",
      aliases: ["sodium lauroyl sarcosinate", "sodium lauryl sarcosinate"],
      category: "surfactant",
      flags: [],
      summary:
        "A sarcosinate surfactant used for foam. Often positioned as milder than SLS.",
    },
    {
      id: "sodium-methyl-cocoyl-taurate",
      name: "Sodium methyl cocoyl taurate",
      aliases: ["sodium methyl cocoyl taurate", "sodium methyl cocoyl taurate"],
      category: "surfactant",
      flags: [],
      summary: "A taurate surfactant used for mild foam in some SLS-free pastes.",
    },
    {
      id: "sodium-lauroyl-glutamate",
      name: "Sodium lauroyl glutamate",
      aliases: ["sodium lauroyl glutamate"],
      category: "surfactant",
      flags: [],
      summary: "An amino-acid-based surfactant used for mild cleansing and foam.",
    },
    {
      id: "slsa",
      name: "Sodium lauryl sulfoacetate (SLSa)",
      aliases: ["sodium lauryl sulfoacetate", "slsa"],
      category: "surfactant",
      flags: [],
      summary:
        "A sulfoacetate surfactant often used in SLS-free formulas. It is not sodium lauryl sulfate.",
    },
    {
      id: "disodium-lauryl-sulfosuccinate",
      name: "Disodium lauryl sulfosuccinate",
      aliases: ["disodium lauryl sulfosuccinate"],
      category: "surfactant",
      flags: [],
      summary: "A sulfosuccinate surfactant used for mild foam in some pastes.",
    },
    {
      id: "coco-glucoside",
      name: "Coco-glucoside",
      aliases: ["coco-glucoside", "coco glucoside"],
      category: "surfactant",
      flags: [],
      summary: "A non-ionic sugar-derived surfactant used in some gentler formulas.",
    },
    {
      id: "decyl-glucoside",
      name: "Decyl glucoside",
      aliases: ["decyl glucoside"],
      category: "surfactant",
      flags: [],
      summary: "A non-ionic glucoside surfactant used for mild cleansing.",
    },
    {
      id: "poloxamer-407",
      name: "Poloxamer 407",
      aliases: ["poloxamer 407", "poloxamer"],
      category: "surfactant",
      flags: [],
      summary:
        "A non-ionic polymer surfactant/solubilizer used to keep flavor oils dispersed.",
    },
    {
      id: "peg-40-hco",
      name: "PEG-40 hydrogenated castor oil",
      aliases: ["peg-40 hydrogenated castor oil", "peg 40 hydrogenated castor oil"],
      category: "surfactant",
      flags: [],
      summary: "A solubilizer used to keep flavor oils mixed in the paste.",
    },
    {
      id: "hydrated-silica",
      name: "Hydrated silica",
      aliases: ["hydrated silica", "silica hydrate"],
      category: "abrasive",
      flags: ["abrasive"],
      summary:
        "The most common toothpaste abrasive and thickener. Cleaning strength depends on particle size and load, often discussed as RDA.",
      detail:
        "Hydrated silica can appear twice in a formula for different jobs: polishing and viscosity. Abrasivity is a formula property, not a single-ingredient score.",
    },
    {
      id: "silica",
      name: "Silica",
      aliases: ["silica", "silicon dioxide"],
      category: "abrasive",
      flags: ["abrasive"],
      summary:
        "Silicon dioxide used as a polish or thickener. Closely related to hydrated silica on toothpaste labels.",
    },
    {
      id: "calcium-carbonate",
      name: "Calcium carbonate",
      aliases: ["calcium carbonate", "chalk"],
      category: "abrasive",
      flags: ["abrasive"],
      summary:
        "A mineral abrasive and filler. Common in some economy and bicarbonate-style pastes.",
    },
    {
      id: "sodium-bicarbonate",
      name: "Sodium bicarbonate",
      aliases: ["sodium bicarbonate", "baking soda", "bicarbonate of soda"],
      category: "abrasive",
      flags: ["abrasive"],
      summary:
        "Baking soda. Used as a mild abrasive and to help neutralize acids. Taste is distinctly salty/alkaline.",
    },
    {
      id: "dicalcium-phosphate",
      name: "Dicalcium phosphate",
      aliases: [
        "dicalcium phosphate",
        "dicalcium phosphate dihydrate",
        "calcium phosphate dibasic",
      ],
      category: "abrasive",
      flags: ["abrasive"],
      summary: "A calcium phosphate abrasive used in some toothpastes.",
    },
    {
      id: "calcium-pyrophosphate",
      name: "Calcium pyrophosphate",
      aliases: ["calcium pyrophosphate"],
      category: "abrasive",
      flags: ["abrasive"],
      summary: "A calcium abrasive used in some polishing formulas.",
    },
    {
      id: "alumina",
      name: "Alumina",
      aliases: ["alumina", "aluminum oxide", "aluminium oxide"],
      category: "abrasive",
      flags: ["abrasive"],
      summary:
        "A relatively hard mineral polish. Less common in daily pastes than hydrated silica.",
    },
    {
      id: "charcoal",
      name: "Activated charcoal",
      aliases: ["activated charcoal", "charcoal", "activated carbon", "carbon"],
      category: "abrasive",
      flags: ["charcoal", "abrasive"],
      summary:
        "Used in some “whitening” pastes for appearance and stain lift. Abrasiveness varies widely by formula; charcoal itself is not a bleach.",
    },
    {
      id: "kaolin",
      name: "Kaolin",
      aliases: ["kaolin", "kaolin clay"],
      category: "abrasive",
      flags: ["abrasive"],
      summary: "A clay mineral used as a mild abrasive or filler in some natural-positioned pastes.",
    },
    {
      id: "microcrystalline-cellulose",
      name: "Microcrystalline cellulose",
      aliases: ["microcrystalline cellulose", "cellulose"],
      category: "abrasive",
      flags: [],
      summary: "A plant-derived cellulose used as a mild abrasive, filler, or tablet binder.",
    },
    {
      id: "polyethylene",
      name: "Polyethylene",
      aliases: ["polyethylene", "pe microbeads"],
      category: "abrasive",
      flags: [],
      summary:
        "Plastic microbeads, historically used as abrasives. Plastic microbeads are banned in US rinse-off cosmetics; seeing this on a current paste is uncommon.",
    },
    {
      id: "water",
      name: "Water (aqua)",
      aliases: ["water", "aqua", "purified water", "deionized water", "aqua water"],
      category: "solvent",
      flags: [],
      summary:
        "The usual base of a toothpaste. Often first on the list because it is present at the highest amount.",
    },
    {
      id: "sorbitol",
      name: "Sorbitol",
      aliases: ["sorbitol"],
      category: "humectant",
      flags: [],
      summary:
        "A sugar alcohol that keeps toothpaste from drying out and adds sweetness. It is not xylitol, though both are polyols.",
    },
    {
      id: "glycerin",
      name: "Glycerin",
      aliases: ["glycerin", "glycerine", "glycerol"],
      category: "humectant",
      flags: [],
      summary: "A humectant that holds moisture in the paste and contributes to texture and sweetness.",
    },
    {
      id: "propylene-glycol",
      name: "Propylene glycol",
      aliases: ["propylene glycol", "pg"],
      category: "humectant",
      flags: [],
      summary:
        "A humectant and solvent for flavors. Distinct from polyethylene glycol (PEG) ingredients.",
    },
    {
      id: "xylitol",
      name: "Xylitol",
      aliases: ["xylitol"],
      category: "sweetener",
      flags: ["xylitol", "sweetener"],
      summary:
        "A sugar alcohol used as a sweetener in some oral-care products. Dose and frequency matter in xylitol research; toothpaste amounts are often small.",
      detail:
        "Xylitol is toxic to dogs if swallowed. Keep toothpaste out of pet reach. Human oral-care studies of xylitol often use gums or lozenges at higher daily doses than a typical toothpaste provides.",
    },
    {
      id: "erythritol",
      name: "Erythritol",
      aliases: ["erythritol"],
      category: "sweetener",
      flags: ["sweetener"],
      summary: "A sugar alcohol sweetener used in some toothpastes and other oral-care products.",
    },
    {
      id: "peg-8",
      name: "PEG-8",
      aliases: ["peg-8", "peg 8", "polyethylene glycol 400"],
      category: "humectant",
      flags: [],
      summary: "A polyethylene glycol used as a humectant and texture aid.",
    },
    {
      id: "peg-12",
      name: "PEG-12",
      aliases: ["peg-12", "peg 12"],
      category: "humectant",
      flags: [],
      summary: "A polyethylene glycol humectant common in gel toothpastes.",
    },
    {
      id: "peg-6",
      name: "PEG-6",
      aliases: ["peg-6", "peg 6"],
      category: "humectant",
      flags: [],
      summary: "A short-chain polyethylene glycol used as a humectant.",
    },
    {
      id: "peg-32",
      name: "PEG-32",
      aliases: ["peg-32", "peg 32"],
      category: "humectant",
      flags: [],
      summary: "A polyethylene glycol used for humectancy and mouthfeel.",
    },
    {
      id: "sodium-saccharin",
      name: "Sodium saccharin",
      aliases: ["sodium saccharin", "saccharin", "saccharin sodium"],
      category: "sweetener",
      flags: ["artificial_sweetener", "sweetener"],
      summary:
        "An artificial high-intensity sweetener. Very common in conventional toothpastes because a tiny amount covers the taste of other ingredients.",
    },
    {
      id: "sucralose",
      name: "Sucralose",
      aliases: ["sucralose"],
      category: "sweetener",
      flags: ["artificial_sweetener", "sweetener"],
      summary: "An artificial high-intensity sweetener used in some toothpastes and mouthwashes.",
    },
    {
      id: "stevia",
      name: "Stevia",
      aliases: [
        "stevia",
        "stevia rebaudiana",
        "stevia rebaudiana leaf",
        "stevia rebaudiana leaf/stem extract",
        "stevia rebaudiana leaf stem extract",
        "rebaudioside a",
        "reb a",
      ],
      category: "sweetener",
      flags: ["sweetener"],
      summary:
        "A plant-derived high-intensity sweetener. Klyyr One+ uses stevia leaf/stem extract rather than saccharin or sucralose.",
    },
    {
      id: "acesulfame-k",
      name: "Acesulfame potassium",
      aliases: ["acesulfame potassium", "acesulfame k", "ace-k"],
      category: "sweetener",
      flags: ["artificial_sweetener", "sweetener"],
      summary: "An artificial high-intensity sweetener, less common in toothpaste than saccharin.",
    },
    {
      id: "cellulose-gum",
      name: "Cellulose gum",
      aliases: [
        "cellulose gum",
        "carboxymethyl cellulose",
        "sodium carboxymethyl cellulose",
        "cmc",
        "sodium cmc",
      ],
      category: "thickener",
      flags: [],
      summary: "A plant-derived binder that thickens toothpaste and keeps it from separating.",
    },
    {
      id: "xanthan-gum",
      name: "Xanthan gum",
      aliases: ["xanthan gum"],
      category: "thickener",
      flags: [],
      summary: "A fermentation-derived gum used to thicken and stabilize the paste.",
    },
    {
      id: "carrageenan",
      name: "Carrageenan",
      aliases: ["carrageenan", "sodium carrageenan", "irish moss"],
      category: "thickener",
      flags: [],
      summary: "A seaweed-derived thickener used in some toothpastes.",
    },
    {
      id: "carbomer",
      name: "Carbomer",
      aliases: ["carbomer", "carbopol"],
      category: "thickener",
      flags: [],
      summary: "A synthetic polymer thickener common in gel toothpastes.",
    },
    {
      id: "pvp",
      name: "PVP (polyvinylpyrrolidone)",
      aliases: ["pvp", "polyvinylpyrrolidone", "povidone"],
      category: "thickener",
      flags: ["whitening"],
      summary:
        "A polymer used as a binder and, in some formulas, to help lift or hold surface stains.",
    },
    {
      id: "hydroxyethylcellulose",
      name: "Hydroxyethylcellulose",
      aliases: ["hydroxyethylcellulose", "hec"],
      category: "thickener",
      flags: [],
      summary: "A cellulose-derived thickener used for viscosity control.",
    },
    {
      id: "acrylates-copolymer",
      name: "Acrylates copolymer",
      aliases: ["acrylates copolymer", "acrylate copolymer"],
      category: "thickener",
      flags: [],
      summary: "A synthetic polymer used to thicken or film-form in some oral-care formulas.",
    },
    {
      id: "sodium-benzoate",
      name: "Sodium benzoate",
      aliases: ["sodium benzoate"],
      category: "preservative",
      flags: [],
      summary:
        "A widely used food and cosmetic preservative that helps keep the formula from spoiling.",
    },
    {
      id: "potassium-sorbate",
      name: "Potassium sorbate",
      aliases: ["potassium sorbate"],
      category: "preservative",
      flags: [],
      summary: "A preservative used against yeasts and molds. Often paired with sodium benzoate.",
    },
    {
      id: "methylparaben",
      name: "Methylparaben",
      aliases: ["methylparaben", "methyl paraben", "sodium methylparaben"],
      category: "preservative",
      flags: ["paraben"],
      summary:
        "A paraben preservative. Parabens are effective antimicrobials; some people prefer to avoid them.",
    },
    {
      id: "propylparaben",
      name: "Propylparaben",
      aliases: ["propylparaben", "propyl paraben", "sodium propylparaben"],
      category: "preservative",
      flags: ["paraben"],
      summary: "A paraben preservative, often used with methylparaben.",
    },
    {
      id: "ethylparaben",
      name: "Ethylparaben",
      aliases: ["ethylparaben", "ethyl paraben"],
      category: "preservative",
      flags: ["paraben"],
      summary: "A paraben preservative used in some oral-care and cosmetic formulas.",
    },
    {
      id: "phenoxyethanol",
      name: "Phenoxyethanol",
      aliases: ["phenoxyethanol"],
      category: "preservative",
      flags: [],
      summary: "A glycol-ether preservative used as an alternative to parabens in some formulas.",
    },
    {
      id: "benzyl-alcohol",
      name: "Benzyl alcohol",
      aliases: ["benzyl alcohol"],
      category: "preservative",
      flags: [],
      summary:
        "Used as a preservative and sometimes as a fragrance component. It can appear on allergen lists.",
    },
    {
      id: "ethylhexylglycerin",
      name: "Ethylhexylglycerin",
      aliases: ["ethylhexylglycerin"],
      category: "preservative",
      flags: [],
      summary: "A booster used with other preservatives; also a mild skin/oral-care conditioner.",
    },
    {
      id: "dmdm-hydantoin",
      name: "DMDM hydantoin",
      aliases: ["dmdm hydantoin"],
      category: "preservative",
      flags: [],
      summary:
        "A formaldehyde-releasing preservative. Uncommon in current mainstream toothpastes.",
    },
    {
      id: "menthol",
      name: "Menthol",
      aliases: ["menthol"],
      category: "flavor",
      flags: [],
      summary:
        "A cooling compound from mint. Provides the “cold” feel people associate with clean teeth; it is not an anticavity active.",
    },
    {
      id: "peppermint",
      name: "Peppermint oil",
      aliases: [
        "mentha piperita",
        "mentha piperita oil",
        "mentha piperita peppermint oil",
        "peppermint oil",
        "peppermint",
      ],
      category: "flavor",
      flags: [],
      summary: "Peppermint essential oil. Used for flavor and aroma.",
    },
    {
      id: "spearmint",
      name: "Spearmint oil",
      aliases: [
        "mentha viridis",
        "mentha viridis leaf oil",
        "mentha spicata",
        "mentha spicata herb oil",
        "spearmint oil",
        "spearmint",
      ],
      category: "flavor",
      flags: [],
      summary: "Spearmint leaf oil. Softer than peppermint; often blended with it.",
    },
    {
      id: "flavor",
      name: "Flavor (aroma)",
      aliases: ["flavor", "flavour", "aroma", "parfum", "fragrance"],
      category: "flavor",
      flags: [],
      summary:
        "A blended flavor system. US toothpaste labels often list this as a single word instead of each aroma chemical.",
    },
    {
      id: "limonene",
      name: "Limonene",
      aliases: ["limonene"],
      category: "flavor",
      flags: [],
      summary:
        "A citrus-scented terpene often declared because it is a recognized fragrance allergen for some people.",
    },
    {
      id: "linalool",
      name: "Linalool",
      aliases: ["linalool"],
      category: "flavor",
      flags: [],
      summary: "A floral terpene alcohol declared as a fragrance allergen in some regions.",
    },
    {
      id: "eugenol",
      name: "Eugenol",
      aliases: ["eugenol"],
      category: "flavor",
      flags: [],
      summary: "A clove-scented compound. Used in flavor; also a known contact allergen for some people.",
    },
    {
      id: "methyl-salicylate",
      name: "Methyl salicylate",
      aliases: ["methyl salicylate", "wintergreen oil", "wintergreen"],
      category: "flavor",
      flags: [],
      summary:
        "Wintergreen flavor. Related to salicylates; relevant if someone has a known salicylate sensitivity.",
    },
    {
      id: "anethole",
      name: "Anethole",
      aliases: ["anethole", "anise"],
      category: "flavor",
      flags: [],
      summary: "An anise/fennel-type flavor compound used in some toothpastes.",
    },
    {
      id: "titanium-dioxide",
      name: "Titanium dioxide",
      aliases: ["titanium dioxide", "ci 77891", "tio2", "ci77891"],
      category: "color",
      flags: ["titanium_dioxide"],
      summary:
        "A white pigment that makes toothpaste opaque. It is a colorant, not a cleaning or anticavity active.",
    },
    {
      id: "blue-1",
      name: "FD&C Blue No. 1",
      aliases: [
        "fd&c blue no. 1",
        "fd&c blue no 1",
        "fd&c blue 1",
        "blue 1",
        "blue no. 1",
        "brilliant blue fcf",
        "ci 42090",
        "ci42090",
      ],
      category: "color",
      flags: ["dye"],
      summary:
        "A synthetic blue dye used for appearance. It has no cleaning function.",
    },
    {
      id: "yellow-5",
      name: "FD&C Yellow No. 5",
      aliases: [
        "fd&c yellow no. 5",
        "fd&c yellow no 5",
        "fd&c yellow 5",
        "yellow 5",
        "tartrazine",
        "ci 19140",
        "ci19140",
      ],
      category: "color",
      flags: ["dye"],
      summary:
        "A synthetic yellow dye (tartrazine). Used for color only; some people specifically avoid azo dyes.",
    },
    {
      id: "yellow-6",
      name: "FD&C Yellow No. 6",
      aliases: [
        "fd&c yellow no. 6",
        "fd&c yellow no 6",
        "fd&c yellow 6",
        "yellow 6",
        "sunset yellow",
        "ci 15985",
        "ci15985",
      ],
      category: "color",
      flags: ["dye"],
      summary: "A synthetic yellow-orange dye used only for appearance.",
    },
    {
      id: "red-40",
      name: "FD&C Red No. 40",
      aliases: [
        "fd&c red no. 40",
        "fd&c red no 40",
        "fd&c red 40",
        "red 40",
        "allura red",
        "ci 16035",
        "ci16035",
      ],
      category: "color",
      flags: ["dye"],
      summary: "A synthetic red dye used in some kids’ and flavored pastes.",
    },
    {
      id: "red-33",
      name: "FD&C Red No. 33",
      aliases: ["fd&c red no. 33", "fd&c red 33", "red 33", "ci 17200", "ci17200"],
      category: "color",
      flags: ["dye"],
      summary: "A synthetic red dye used for color only.",
    },
    {
      id: "green-3",
      name: "FD&C Green No. 3",
      aliases: ["fd&c green no. 3", "fd&c green 3", "green 3", "ci 42053", "ci42053"],
      category: "color",
      flags: ["dye"],
      summary: "A synthetic green dye used for appearance.",
    },
    {
      id: "mica",
      name: "Mica",
      aliases: ["mica", "ci 77019", "ci77019"],
      category: "color",
      flags: [],
      summary: "A mineral used for shimmer or opacity in some pastes. Not an active ingredient.",
    },
    {
      id: "iron-oxides",
      name: "Iron oxides",
      aliases: ["iron oxides", "iron oxide", "ci 77491", "ci 77492", "ci 77499"],
      category: "color",
      flags: [],
      summary: "Mineral pigments used for brown, red, yellow, or black color.",
    },
    {
      id: "sodium-hydroxide",
      name: "Sodium hydroxide",
      aliases: ["sodium hydroxide"],
      category: "pH",
      flags: [],
      summary: "Used in small amounts to adjust pH so the formula stays stable and comfortable.",
    },
    {
      id: "phosphoric-acid",
      name: "Phosphoric acid",
      aliases: ["phosphoric acid"],
      category: "pH",
      flags: [],
      summary: "An acid used to adjust pH in some gel toothpastes.",
    },
    {
      id: "citric-acid",
      name: "Citric acid",
      aliases: ["citric acid"],
      category: "pH",
      flags: [],
      summary: "A pH adjuster and chelator. In toothpaste it is a formula aid, not a drink acid.",
    },
    {
      id: "sodium-citrate",
      name: "Sodium citrate",
      aliases: ["sodium citrate", "trisodium citrate"],
      category: "pH",
      flags: [],
      summary: "A citrate salt used to buffer pH and bind metal ions.",
    },
    {
      id: "sodium-phosphate",
      name: "Sodium phosphate",
      aliases: ["sodium phosphate", "disodium phosphate", "trisodium phosphate"],
      category: "pH",
      flags: [],
      summary: "Phosphate salts used to buffer pH or as part of a cleaning system.",
    },
    {
      id: "triclosan",
      name: "Triclosan",
      aliases: ["triclosan"],
      category: "antimicrobial",
      flags: ["triclosan"],
      summary:
        "An antibacterial previously used in some toothpastes. The FDA has restricted consumer antiseptic uses; it is uncommon in current US pastes.",
    },
    {
      id: "cpc",
      name: "Cetylpyridinium chloride",
      aliases: ["cetylpyridinium chloride", "cpc"],
      category: "antimicrobial",
      flags: [],
      summary:
        "A quaternary ammonium antiseptic more common in mouthwashes than toothpastes. Used for plaque and malodor control.",
    },
    {
      id: "aloe",
      name: "Aloe vera",
      aliases: [
        "aloe vera",
        "aloe barbadensis",
        "aloe barbadensis leaf juice",
        "aloe barbadensis leaf extract",
      ],
      category: "other",
      flags: [],
      summary:
        "Aloe leaf juice or extract, used for soothing positioning. It is not an anticavity active.",
    },
    {
      id: "tea-tree",
      name: "Tea tree oil",
      aliases: ["tea tree", "melaleuca", "melaleuca alternifolia", "tea tree oil"],
      category: "other",
      flags: [],
      summary:
        "Melaleuca essential oil, used for aroma and antimicrobial positioning. Can be sensitizing for some people.",
    },
    {
      id: "cocos-nucifera",
      name: "Coconut oil",
      aliases: ["cocos nucifera", "coconut oil", "cocos nucifera oil"],
      category: "other",
      flags: [],
      summary: "Coconut oil, used as an emollient or in oil-pulling-style pastes.",
    },
    {
      id: "green-tea",
      name: "Green tea extract",
      aliases: ["camellia sinensis", "green tea", "camellia sinensis leaf extract"],
      category: "other",
      flags: [],
      summary: "Green tea leaf extract, used for antioxidant or flavor positioning.",
    },
    {
      id: "sodium-chloride",
      name: "Sodium chloride",
      aliases: ["sodium chloride", "salt", "sea salt"],
      category: "other",
      flags: [],
      summary: "Salt. Used for taste, ionic strength, or in some “salt toothpaste” formulas.",
    },
    {
      id: "alcohol",
      name: "Alcohol",
      aliases: ["alcohol", "ethanol", "alcohol denat", "alcohol denat."],
      category: "solvent",
      flags: [],
      summary:
        "Ethanol, more common in mouthwashes than toothpaste. Used as a solvent. Drying for some people.",
    },
    {
      id: "sodium-phytate",
      name: "Sodium phytate",
      aliases: ["sodium phytate", "phytic acid"],
      category: "tartar",
      flags: [],
      summary: "A chelator used to bind calcium and help with stain or tartar control.",
    },
    {
      id: "tocopherol",
      name: "Tocopherol (vitamin E)",
      aliases: ["tocopherol", "tocopheryl acetate", "vitamin e"],
      category: "other",
      flags: [],
      summary: "Vitamin E, used as an antioxidant to protect the formula or for label positioning.",
    },
    {
      id: "sodium-hyaluronate",
      name: "Sodium hyaluronate",
      aliases: ["sodium hyaluronate", "hyaluronic acid"],
      category: "other",
      flags: [],
      summary: "A humectant/moisturizer used in some gum-comfort formulas.",
    },
    {
      id: "hydroxyapatite-fluoro",
      name: "Fluorapatite",
      aliases: ["fluorapatite", "fluoroapatite"],
      category: "remineralizing",
      flags: ["fluoride", "hap"],
      summary:
        "A fluoride-substituted relative of hydroxyapatite. Rare as a listed toothpaste ingredient.",
    },
    {
      id: "novamin",
      name: "Calcium sodium phosphosilicate",
      aliases: [
        "calcium sodium phosphosilicate",
        "novamin",
        "bioactive glass",
      ],
      category: "remineralizing",
      flags: ["sensitivity"],
      summary:
        "A bioactive glass (often called NovaMin) studied for sensitivity and surface mineral deposition.",
    },
    {
      id: "acp",
      name: "Amorphous calcium phosphate",
      aliases: ["amorphous calcium phosphate", "acp", "cpp-acp", "casein phosphopeptide"],
      category: "remineralizing",
      flags: [],
      summary:
        "A calcium phosphate system used in some remineralizing products, sometimes with casein peptides (CPP-ACP).",
    },
    {
      id: "theobromine",
      name: "Theobromine",
      aliases: ["theobromine"],
      category: "remineralizing",
      flags: [],
      summary:
        "A cocoa-derived alkaloid used in a small number of toothpaste formulas. Evidence is more limited than for fluoride or hydroxyapatite.",
    },
  ];

  const bound = new WeakSet();

  /** @type {Map<string, typeof INGREDIENTS[number]>} */
  const exactIndex = new Map();
  /** @type {Array<{alias: string, ingredient: typeof INGREDIENTS[number]}>} */
  const aliasList = [];

  INGREDIENTS.forEach((ingredient) => {
    const names = [ingredient.name, ...ingredient.aliases];
    names.forEach((alias) => {
      const key = normalize(alias);
      if (!key) return;
      if (!exactIndex.has(key)) exactIndex.set(key, ingredient);
      aliasList.push({ alias: key, ingredient });
    });
  });

  aliasList.sort((a, b) => b.alias.length - a.alias.length);

  /**
   * @param {string} value
   * @returns {string}
   */
  function normalize(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " and ")
      .replace(/no\./g, "no")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function stripConcentration(raw) {
    return raw
      .replace(/\((?:[^)]*(?:%|ppm|w\/v)[^)]*)\)/gi, " ")
      .replace(/\d+(?:\.\d+)?\s*%(?:\s*w\/v)?/gi, " ")
      .replace(/\d+\s*ppm/gi, " ")
      .replace(/\b\d+(?:\.\d+)?\s*(?:mg|g)\b/gi, " ");
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function extractNote(raw) {
    const percent = raw.match(/(\d+(?:\.\d+)?)\s*%/);
    const ppm = raw.match(/(\d+)\s*ppm/i);
    if (ppm) return `${ppm[1]} ppm`;
    if (percent) return `${percent[1]}%`;
    const nano = /\bnano\b/i.test(raw);
    const micro = /\bmicro\b/i.test(raw);
    if (nano && micro) return "nano + micro";
    if (nano) return "nano";
    if (micro) return "micro";
    return "";
  }

  /**
   * Split an INCI-style list on commas/semicolons/newlines that are not inside parentheses.
   * @param {string} text
   * @returns {string[]}
   */
  function splitList(text) {
    const cleaned = String(text || "")
      .replace(/\r/g, "")
      .replace(/\b(active|inactive|other)\s+ingredients?\s*:/gi, "\n")
      .replace(/\bpurpose\s*:/gi, "\n")
      .replace(/\bdrug facts\b/gi, "\n")
      .replace(/[•·▪◦]/g, "\n")
      .replace(/\t/g, " ");

    const parts = [];
    let current = "";
    let depth = 0;

    for (let i = 0; i < cleaned.length; i += 1) {
      const char = cleaned[i];
      if (char === "(") depth += 1;
      if (char === ")") depth = Math.max(0, depth - 1);

      if (depth === 0 && (char === "," || char === ";" || char === "\n")) {
        if (current.trim()) parts.push(current.trim());
        current = "";
        continue;
      }
      current += char;
    }
    if (current.trim()) parts.push(current.trim());

    return parts
      .map((part) =>
        part
          .replace(/^\s*\d+\s*[.)]\s*/, "")
          .replace(/^\s*[\-–—]\s*/, "")
          .replace(/\s+/g, " ")
          .trim()
      )
      .filter((part) => {
        if (!part) return false;
        const key = normalize(stripConcentration(part));
        return key && !SKIP_TOKENS.has(key);
      });
  }

  /**
   * @param {string} token
   * @returns {typeof INGREDIENTS[number] | null}
   */
  function matchToken(token) {
    const stripped = stripConcentration(token);
    const candidates = [stripped];

    const paren = stripped.match(/^(.*?)\((.*)\)\s*(.*)$/);
    if (paren) {
      candidates.push(`${paren[1]} ${paren[3]}`.trim());
      candidates.push(paren[2]);
      candidates.push(paren[1].trim());
    }

    for (const candidate of candidates) {
      const key = normalize(candidate);
      if (key && exactIndex.has(key)) return exactIndex.get(key) || null;
    }

    const key = normalize(stripped);
    if (!key) return null;

    let best = null;
    let bestLen = 0;
    for (let i = 0; i < aliasList.length; i += 1) {
      const { alias, ingredient } = aliasList[i];
      const aliasOk = alias.length >= 4 || SHORT_ALIASES.has(alias);
      if (!aliasOk) continue;

      const tokenContainsAlias = key.includes(alias);
      const aliasContainsToken =
        alias.includes(key) && (key.length >= 5 || SHORT_ALIASES.has(key));

      if (!tokenContainsAlias && !aliasContainsToken) continue;

      const score = tokenContainsAlias ? alias.length + 10 : key.length;
      if (score > bestLen) {
        best = ingredient;
        bestLen = score;
      }
    }
    return best;
  }

  /**
   * @param {string} text
   */
  function analyze(text) {
    const tokens = splitList(text);
    /** @type {Map<string, {ingredient: typeof INGREDIENTS[number], labels: string[], notes: string[]}>} */
    const matched = new Map();
    const unmatched = [];

    tokens.forEach((token) => {
      const ingredient = matchToken(token);
      const note = extractNote(token);
      if (!ingredient) {
        unmatched.push(token);
        return;
      }
      const existing = matched.get(ingredient.id);
      if (existing) {
        if (!existing.labels.includes(token)) existing.labels.push(token);
        if (note && !existing.notes.includes(note)) existing.notes.push(note);
      } else {
        matched.set(ingredient.id, {
          ingredient,
          labels: [token],
          notes: note ? [note] : [],
        });
      }
    });

    const flags = new Set();
    matched.forEach((entry) => {
      entry.ingredient.flags.forEach((flag) => flags.add(flag));
    });

    const groups = [];
    CATEGORY_ORDER.forEach((category) => {
      const items = [];
      matched.forEach((entry) => {
        if (entry.ingredient.category === category) items.push(entry);
      });
      if (items.length) groups.push({ category, label: CATEGORY_LABELS[category], items });
    });

    return {
      tokenCount: tokens.length,
      matchedCount: matched.size,
      unmatched,
      groups,
      flags,
      snapshots: SNAPSHOTS.map((snapshot) => ({
        ...snapshot,
        present: flags.has(snapshot.flag),
      })),
    };
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   */
  function fill(template, vars) {
    return String(template || "").replace(/\{(\w+)\}|%(\w+)%/g, (_, braceKey, pctKey) => {
      const value = vars[braceKey || pctKey];
      return value == null ? "" : String(value);
    });
  }

  /**
   * @param {ReturnType<typeof analyze>} result
   * @param {string} origin
   */
  function resultsPlainText(result, origin) {
    const present = result.snapshots.filter((item) => item.present).map((item) => item.label);
    const absent = result.snapshots.filter((item) => !item.present).map((item) => item.label);
    const lines = [
      "Toothpaste ingredient check",
      `${result.tokenCount} names pasted · ${result.matchedCount} recognized`,
      "",
      present.length ? `Listed: ${present.join(", ")}` : "Listed: —",
      absent.length ? `Not listed: ${absent.join(", ")}` : "",
      "",
    ];

    result.groups.forEach((group) => {
      lines.push(group.label);
      group.items.forEach((item) => {
        const note = item.notes.length ? ` (${item.notes.join(", ")})` : "";
        lines.push(`• ${item.ingredient.name}${note} — ${item.ingredient.summary}`);
      });
      lines.push("");
    });

    if (result.unmatched.length) {
      lines.push("Not in the directory");
      result.unmatched.forEach((name) => lines.push(`• ${name}`));
      lines.push("");
    }

    lines.push(`Checked with the free Toothpaste Ingredient Checker at ${origin}`);
    lines.push("Runs in your browser. Not medical advice.");
    return lines.filter((line, index, arr) => !(line === "" && arr[index - 1] === "")).join("\n");
  }

  /**
   * @param {ReturnType<typeof analyze>} result
   * @param {Record<string, string>} strings
   * @returns {string[]}
   */
  function offerLines(result, strings) {
    const flags = result.flags;
    const lines = [];
    if (flags.has("sls") && strings.offerSls) lines.push(strings.offerSls);
    if (flags.has("dye") && strings.offerDye) lines.push(strings.offerDye);
    if (!flags.has("hap") && strings.offerNoHap) lines.push(strings.offerNoHap);
    if (flags.has("fluoride") && strings.offerFluoride) lines.push(strings.offerFluoride);
    if (flags.has("titanium_dioxide") && strings.offerTio2) lines.push(strings.offerTio2);
    if (!lines.length && flags.has("hap") && strings.offerHap) lines.push(strings.offerHap);
    if (!lines.length && strings.offerDefault) lines.push(strings.offerDefault);
    return lines.slice(0, 2);
  }

  /**
   * @param {ReturnType<typeof analyze>} result
   * @param {{ compareUrl?: string, compareHapUrl?: string, compareFluorideUrl?: string }} config
   * @returns {string}
   */
  function compareHref(result, config) {
    if (result.flags.has("hap") && config.compareHapUrl) return config.compareHapUrl;
    if (result.flags.has("fluoride") && config.compareFluorideUrl) return config.compareFluorideUrl;
    return config.compareUrl || "/pages/compare";
  }

  /**
   * @param {ReturnType<typeof analyze>} result
   */
  function publishCheck(result) {
    const present = result.snapshots.filter((item) => item.present).map((item) => item.id);
    const payload = {
      content_type: "ingredient_check",
      item_id: present.join(",") || "none",
    };
    if (typeof window.Shopify?.analytics?.publish === "function") {
      window.Shopify.analytics.publish("select_content", payload);
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function mount(root) {
    if (bound.has(root)) return;
    bound.add(root);

    const form = root.querySelector("[data-checker-form]");
    const textarea = root.querySelector("[data-checker-input]");
    const after = root.querySelector("[data-checker-after]");
    const snapshot = root.querySelector("[data-checker-snapshot]");
    const groups = root.querySelector("[data-checker-groups]");
    const offerBody = root.querySelector("[data-checker-offer-body]");
    const compareLink = root.querySelector("[data-checker-compare]");
    const samples = root.querySelector("[data-checker-samples]");
    const configNode = root.querySelector("[data-checker-config]");
    const copyBtn = root.querySelector("[data-checker-copy]");
    if (
      !(form instanceof HTMLFormElement) ||
      !(textarea instanceof HTMLTextAreaElement) ||
      !(after instanceof HTMLElement) ||
      !(snapshot instanceof HTMLElement) ||
      !(groups instanceof HTMLElement)
    ) {
      return;
    }

    let config = { samples: [], strings: {} };
    try {
      if (configNode) config = JSON.parse(configNode.textContent || "{}");
    } catch {
      config = { samples: [], strings: {} };
    }
    const strings = config.strings || {};

    const setSamplePressed = (active) => {
      if (!samples) return;
      samples.querySelectorAll("[data-sample]").forEach((node) => {
        if (node instanceof HTMLButtonElement) {
          node.setAttribute("aria-pressed", node === active ? "true" : "false");
        }
      });
    };

    const revealResults = () => {
      after.hidden = false;
      const title = snapshot.querySelector("[data-checker-result-title]");
      if (title instanceof HTMLElement) title.focus({ preventScroll: true });
      after.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "start",
      });
    };

    /**
     * @param {{ track?: boolean }} [options]
     */
    const render = (options = {}) => {
      const value = textarea.value.trim();
      if (!value) {
        after.hidden = true;
        snapshot.innerHTML = "";
        groups.innerHTML = "";
        if (copyBtn instanceof HTMLButtonElement) copyBtn.hidden = true;
        setSamplePressed(null);
        return;
      }

      const result = analyze(value);
      const snapshotHtml = result.snapshots
        .map((item) => {
          const state = item.present
            ? strings.listed || "Listed"
            : strings.notListed || "Not listed";
          const cls = item.present ? "is-present" : "is-absent";
          return `<li class="ing-check__snap ${cls}"><span class="ing-check__snap-label">${escapeHtml(
            item.label
          )}</span><span class="ing-check__snap-state">${escapeHtml(state)}</span></li>`;
        })
        .join("");

      const groupsHtml = result.groups
        .map((group) => {
          const items = group.items
            .map((item) => {
              const note = item.notes.length
                ? `<span class="k-tiny ing-check__note">${escapeHtml(item.notes.join(" · "))}</span>`
                : "";
              const asWritten =
                item.labels[0] && normalize(item.labels[0]) !== normalize(item.ingredient.name)
                  ? `<p class="ing-check__as-written">On the label: ${escapeHtml(item.labels.join("; "))}</p>`
                  : "";
              const body = item.ingredient.detail
                ? `<details class="ing-check__details"><summary>More</summary><p>${escapeHtml(
                    item.ingredient.detail
                  )}</p></details>`
                : "";
              return `<article class="ing-check__item">
                <div class="ing-check__item-head">
                  <h3 class="ing-check__item-name">${escapeHtml(item.ingredient.name)}</h3>
                  ${note}
                </div>
                <p class="ing-check__item-summary">${escapeHtml(item.ingredient.summary)}</p>
                ${asWritten}
                ${body}
              </article>`;
            })
            .join("");
          return `<section class="ing-check__group">
            <h3 class="k-tiny ing-check__group-label">${escapeHtml(group.label)}</h3>
            <div class="ing-check__group-list">${items}</div>
          </section>`;
        })
        .join("");

      const unmatchedHtml = result.unmatched.length
        ? `<section class="ing-check__group ing-check__group--unmatched">
            <h3 class="k-tiny ing-check__group-label">${escapeHtml(
              strings.unmatchedHeading || "Not in our directory"
            )}</h3>
            <p class="ing-check__unmatched-note">${escapeHtml(
              strings.unmatchedNote ||
                "These names did not match the directory. They may be uncommon INCI names, proprietary blends, or typos."
            )}</p>
            <ul class="ing-check__unmatched">${result.unmatched
              .map((name) => `<li>${escapeHtml(name)}</li>`)
              .join("")}</ul>
          </section>`
        : "";

      const meta = fill(strings.meta || "{tokens} names · {matched} recognized", {
        tokens: result.tokenCount,
        matched: result.matchedCount,
        unmatched: result.unmatched.length,
      });

      snapshot.innerHTML = `
        <div class="ing-check__result-head">
          <h2 class="k-tiny ing-check__result-kicker" tabindex="-1" data-checker-result-title>${escapeHtml(
            strings.resultKicker || "Snapshot"
          )}</h2>
          <p class="ing-check__result-meta">${escapeHtml(meta)}</p>
        </div>
        <ul class="ing-check__snaps">${snapshotHtml}</ul>
      `;
      groups.innerHTML = `${groupsHtml}${unmatchedHtml}`;
      after.hidden = false;

      if (offerBody instanceof HTMLElement) {
        const lines = offerLines(result, strings);
        offerBody.innerHTML =
          lines.length > 1
            ? `<ul class="ing-check__offer-points">${lines
                .map((line) => `<li>${escapeHtml(line)}</li>`)
                .join("")}</ul>`
            : `<p>${escapeHtml(lines[0] || "")}</p>`;
      }

      if (compareLink instanceof HTMLAnchorElement) {
        compareLink.href = compareHref(result, config);
      }

      if (copyBtn instanceof HTMLButtonElement) {
        copyBtn.hidden = false;
        copyBtn.dataset.plain = resultsPlainText(
          result,
          window.location.origin + window.location.pathname
        );
      }

      if (options.track) publishCheck(result);
    };

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      render({ track: true });
      if (textarea.value.trim()) revealResults();
    });

    textarea.addEventListener("keydown", (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        form.requestSubmit();
      }
    });

    textarea.addEventListener("input", () => {
      const pressed = samples && samples.querySelector('[aria-pressed="true"]');
      if (!(pressed instanceof HTMLButtonElement)) return;
      if (pressed.getAttribute("data-sample") !== textarea.value) setSamplePressed(null);
    });

    const clearBtn = root.querySelector("[data-checker-clear]");
    if (clearBtn instanceof HTMLButtonElement) {
      clearBtn.addEventListener("click", () => {
        textarea.value = "";
        textarea.focus();
        render();
      });
    }

    if (samples) {
      samples.addEventListener("click", (event) => {
        const button = event.target instanceof Element ? event.target.closest("[data-sample]") : null;
        if (!(button instanceof HTMLButtonElement)) return;
        const list = button.getAttribute("data-sample") || "";
        textarea.value = list;
        setSamplePressed(button);
        render({ track: true });
        revealResults();
      });
    }

    if (copyBtn instanceof HTMLButtonElement) {
      copyBtn.addEventListener("click", async () => {
        const text = copyBtn.dataset.plain || "";
        if (!text) return;
        try {
          await navigator.clipboard.writeText(text);
          const label = copyBtn.querySelector("[data-checker-copy-label]");
          const original = (label || copyBtn).textContent;
          (label || copyBtn).textContent = strings.copied || "Copied";
          window.setTimeout(() => {
            (label || copyBtn).textContent = original;
          }, 1600);
        } catch {
          const label = copyBtn.querySelector("[data-checker-copy-label]");
          (label || copyBtn).textContent = strings.copyFailed || "Copy failed";
        }
      });
    }
  }

  /**
   * @param {string} value
   */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function scan(scope) {
    (scope || document).querySelectorAll("[data-ingredient-checker]").forEach((node) => {
      if (node instanceof HTMLElement) mount(node);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document));
  } else {
    scan(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof Element) scan(event.target);
  });

  window.KlyyrIngredientChecker = { analyze, splitList, matchToken };
})();
